import type { ReactNode } from 'react';
import {
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Image,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  Activity,
  Calendar,
  CheckCircle2,
  Clock3,
  Layers,
  Ruler,
  Scale,
} from 'lucide-react';
import type {
  PatientMeasurementRecord,
  PatientNutritionMetrics,
  PatientSessionDomainStat,
  PatientSessionsSummary,
} from '../types';
import {
  formatDateValue,
  formatMeasurementField,
  formatMetricValue,
} from '../utils/formatters';
import { PATIENT_SESSION_STATUS_LABELS } from '../utils/sessions';
import { PatientStatTile, type PatientStatTileProps } from './PatientStatTile';

interface PatientOverviewTabProps {
  latestMeasurement: PatientMeasurementRecord | null;
  measurementsCount: number;
  metrics: PatientNutritionMetrics;
  sessionsSummary: PatientSessionsSummary;
  onOpenBodyDomain: () => void;
  onOpenSessionsDomain: () => void;
}

function DomainOverviewColumn({
  title,
  badge,
  icon,
  iconColor,
  heroLabel,
  heroValue,
  heroHint,
  stats,
  statsContent,
  actionLabel,
  onAction,
}: {
  title: string;
  badge: string;
  icon: typeof Ruler;
  iconColor: string;
  heroLabel: string;
  heroValue: string;
  heroHint?: string;
  stats?: PatientStatTileProps[];
  statsContent?: ReactNode;
  actionLabel: string;
  onAction: () => void;
}) {
  const Icon = icon;

  return (
    <Card withBorder p='lg' radius='md' h='100%'>
      <Stack gap='lg' h='100%'>
        <Group justify='space-between' align='flex-start' wrap='wrap'>
          <Group gap='sm' align='center'>
            <ThemeIcon variant='light' color={iconColor} size='xl' radius='md'>
              <Icon size={20} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text fw={600} size='lg'>
                {title}
              </Text>
              <Badge variant='light' color={iconColor} size='sm'>
                {badge}
              </Badge>
            </Stack>
          </Group>
        </Group>

        <Stack gap={4}>
          <Text size='xs' c='dimmed' tt='uppercase' fw={700}>
            {heroLabel}
          </Text>
          <Text fw={800} size='2rem' lh={1.1}>
            {heroValue}
          </Text>
          {heroHint != null && heroHint !== '' && (
            <Text size='sm' c='dimmed'>
              {heroHint}
            </Text>
          )}
        </Stack>

        <Stack gap='md' style={{ flex: 1 }}>
          {statsContent ?? (
            <SimpleGrid cols={{ base: 1, xs: 2 }} spacing='md'>
              {(stats ?? []).map(stat => (
                <PatientStatTile key={stat.label} {...stat} />
              ))}
            </SimpleGrid>
          )}
        </Stack>

        <Button variant='light' onClick={onAction}>
          {actionLabel}
        </Button>
      </Stack>
    </Card>
  );
}

function DomainLogosGroup({
  domains,
}: {
  domains: PatientSessionDomainStat[];
}) {
  if (domains.length === 0) {
    return (
      <Text size='sm' c='dimmed'>
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap='wrap' align='flex-start'>
      {domains.map(domain => (
        <Tooltip
          key={domain.domainKey}
          label={`${domain.label} · ${domain.count} sessioni`}
          withArrow
        >
          <Paper
            withBorder
            p={6}
            radius='md'
            style={{ flexShrink: 0, lineHeight: 0 }}
          >
            <Image
              src={domain.iconUrl}
              w={28}
              h={28}
              fit='contain'
              alt={domain.label}
            />
          </Paper>
        </Tooltip>
      ))}
    </Group>
  );
}

function SessionsOverviewStats({
  sessionsSummary,
}: {
  sessionsSummary: PatientSessionsSummary;
}) {
  return (
    <Stack gap='md'>
      <SimpleGrid cols={{ base: 1, xs: 2 }} spacing='md'>
        <PatientStatTile
          label='Programmate'
          value={String(sessionsSummary.queued)}
          icon={Clock3}
          color='orange'
        />
        <PatientStatTile
          label='Completate'
          value={String(sessionsSummary.completed)}
          icon={CheckCircle2}
          color='teal'
        />
      </SimpleGrid>

      <Stack gap='xs'>
        <Text size='xs' c='dimmed' tt='uppercase' fw={700}>
          App in cui l&apos;utente compare
        </Text>
        <DomainLogosGroup domains={sessionsSummary.domains} />
      </Stack>
    </Stack>
  );
}

const formatBmiOverviewValue = (metrics: PatientNutritionMetrics) => {
  if (metrics.bmi == null) {
    return '—';
  }

  const formattedBmi = formatMetricValue(metrics.bmi);
  if (formattedBmi === '-') {
    return '—';
  }

  return metrics.bmiCategory != null
    ? `${formattedBmi} · ${metrics.bmiCategory}`
    : formattedBmi;
};

export function PatientOverviewTab({
  latestMeasurement,
  measurementsCount,
  metrics,
  sessionsSummary,
  onOpenBodyDomain,
  onOpenSessionsDomain,
}: PatientOverviewTabProps) {
  const latestWeightField =
    latestMeasurement?.fields.find(field => field.metricType === 'weight_kg') ??
    null;
  const latestWaistField =
    latestMeasurement?.fields.find(field => field.metricType === 'waist_cm') ??
    null;
  const activeSession = sessionsSummary.activeSession;
  const activeSessionsCount =
    sessionsSummary.inProgress + sessionsSummary.queued;

  const bodyHeroValue = latestWeightField
    ? formatMeasurementField(latestWeightField)
    : '—';
  const bodyHeroHint =
    latestMeasurement == null
      ? 'Registra la prima rilevazione per attivare metriche e storico.'
      : metrics.bmiCategory != null
        ? `Classificazione BMI: ${metrics.bmiCategory}`
        : undefined;

  const sessionsHeroValue =
    activeSessionsCount > 0 && activeSession != null
      ? `#${activeSession.id}`
      : String(sessionsSummary.total);
  const sessionsHeroLabel =
    activeSessionsCount > 0 && activeSession != null
      ? 'Sessione attiva'
      : 'Totale sessioni';
  const sessionsHeroHint =
    activeSessionsCount > 0 && activeSession != null
      ? (PATIENT_SESSION_STATUS_LABELS[activeSession.status] ??
        activeSession.status)
      : sessionsSummary.total === 0
        ? 'Nessuna sessione collegata a questo utente.'
        : sessionsSummary.latestSession?.updatedAt != null
          ? `Ultima attività ${formatDateValue(sessionsSummary.latestSession.updatedAt)}`
          : undefined;

  const bodyStats: PatientStatTileProps[] = [
    {
      label: 'BMI',
      value: formatBmiOverviewValue(metrics),
      icon: Scale,
      color: 'teal',
    },
    {
      label: 'Vita',
      value: latestWaistField ? formatMeasurementField(latestWaistField) : '—',
      icon: Ruler,
      color: 'orange',
    },
    {
      label: 'Ultima rilevazione',
      value: latestMeasurement?.createdAt
        ? formatDateValue(latestMeasurement.createdAt)
        : '—',
      icon: Calendar,
      color: 'violet',
    },
    {
      label: 'Rilevazioni',
      value: String(measurementsCount),
      icon: Layers,
      color: 'blue',
    },
  ];

  const sessionsStatsContent = (
    <SessionsOverviewStats sessionsSummary={sessionsSummary} />
  );

  return (
    <Grid>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <DomainOverviewColumn
          title='Misure corporee'
          badge={
            measurementsCount > 0
              ? `${measurementsCount} rilevazioni`
              : 'Nessuna rilevazione'
          }
          icon={Ruler}
          iconColor='orange'
          heroLabel='Ultimo peso'
          heroValue={bodyHeroValue}
          heroHint={bodyHeroHint}
          stats={bodyStats}
          actionLabel='Vai a Misure corporee'
          onAction={onOpenBodyDomain}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <DomainOverviewColumn
          title='Sessioni'
          badge={
            activeSessionsCount > 0
              ? `${activeSessionsCount} attive`
              : `${sessionsSummary.total} totali`
          }
          icon={Activity}
          iconColor='blue'
          heroLabel={sessionsHeroLabel}
          heroValue={sessionsHeroValue}
          heroHint={sessionsHeroHint}
          statsContent={sessionsStatsContent}
          actionLabel='Vai a Sessioni'
          onAction={onOpenSessionsDomain}
        />
      </Grid.Col>
    </Grid>
  );
}
